import React, { useEffect, useRef, useState } from "react";
import InputField from "components/form/InputField";
import InputSelect from "components/form/InputSelect";
import { valuesProps } from "types/paymentMethods";
import { MastercardIcon } from "icons/payments/MastercardIcon";
import { VisaIcon } from "icons/payments/VisaIcon";

import JCB from "icons/payments/JCB.svg";
import amex from "icons/payments/amex.svg";
import dinersClub from "icons/payments/dinersClub.svg";
import Discover from "icons/payments/Discover";
import {
  countryListGet,
  useCountryList,
} from "store/slices/paymentMethodSlice";
import { useAppDispatch } from "store/hooks";
import UnionPay from "icons/payments/UnionPay";
import { usePortalSettingsData } from "store/slices/dashboardSlice";
import { AddressDetails, countryAddressList } from "utils/countryAddressList";
import { SettingsValue } from "types/payments";
import { detectCountry } from "utils/HelperFunctions";
import { countryAndState } from "store/slices/documentsSlice";
import { useTid } from "utils/testid";

interface CardFormProps {
  handleChange: (e: any, value: string) => void;
  isEdit?: boolean | undefined;
  cardNumber: string;
  values: valuesProps;
  iconType: string;
  isDateErr: boolean;
  selectedState: any;
  setSelectedState: any;
  validateCard?: any;
  isErr?: number | -1;
  cardErrMsg?: string | "";
  disableInputFields?: boolean;
  show?: boolean;
}

const CardFormComponent = ({
  handleChange,
  isEdit,
  cardNumber,
  values,
  iconType,
  selectedState,
  setSelectedState,
  validateCard = () => {},
  isErr,
  cardErrMsg,
  disableInputFields,
  show,
}: CardFormProps) => {
  const tid = useTid();
  const countryList = useCountryList();
  const dispatch = useAppDispatch();
  const portalSettings = usePortalSettingsData();
  const [fieldArr, setFieldArr] = useState<string[]>();
  const [fieldObj, setFieldObj] = useState<AddressDetails>();
  const onCountryListGet = async () => {
    try {
      await dispatch(countryListGet()).unwrap();
    } catch (error: any) {
      console.log("conntry list get err: -->", error?.message);
    }
  };
  const [states, setStates] = useState<any>([]);
  const [countriesWithStates, setCountriesWithStates] = useState<any[]>([]);
  const initCountryRef = useRef(false);
  const autoDetectRef = useRef(false);

  useEffect(() => {
    autoDetectRef.current = false;
  }, []);

  const setCountry = async () => {
    const userCountry = await detectCountry();
    userCountry && handleChange({ value: userCountry }, "x_country");
  };

  useEffect(() => {
    if (countryList.length === 0) {
      onCountryListGet();
      return;
    }
    if (
      !isEdit &&
      !autoDetectRef.current &&
      (!values?.x_country || values?.x_country === "US")
    ) {
      setCountry();
      autoDetectRef.current = true;
    }
  }, [countryList]);

  useEffect(() => {
    if (values?.x_country) {
      let data = countryAddressList()[values?.x_country || "US"];
      if (data) {
        const arr = Object.keys(data).map((field) => field);
        const obj = countryAddressList()[values?.x_country || "US"];
        setFieldArr(arr);
        setFieldObj(obj);
      }
    }
    if (isEdit && values?.x_country) {
      const nStates = getStatesForCountryCode(values?.x_country);

      setStates(nStates);
      if (values?.x_state) {
        const selectedStateObject: any = nStates.find(
          (state: any) => state.name === values?.x_state
        );
        if (selectedStateObject) {
          setSelectedState(selectedStateObject);
          handleChange(selectedStateObject?.name, "x_state");
        } else {
          const obj = nStates.find(
            (state: any) => state.state_code === values?.x_state
          );
          if (obj) {
            setSelectedState(obj);
            handleChange(obj?.name, "x_state");
          }
        }
      }
    }
  }, [isEdit, values?.x_country, values?.x_state, countriesWithStates]);

  useEffect(() => {
    if (show && !isEdit && countryList?.length > 0) {
      setCountry();
      autoDetectRef.current = true;
    }
  }, [show, isEdit, countryList]);

  const getPostalCode = () => {
    if (fieldArr && fieldArr?.includes("CEDEX")) return "CEDEX";
    if (fieldArr && fieldArr?.includes("ZIP")) return "ZIP";
    if (fieldArr && fieldArr?.includes("Eircode")) return "Eircode";
    if (fieldArr && fieldArr?.includes("PIN")) return "PIN";
    if (fieldArr && fieldArr?.includes("Postal code")) return "Postal code";

    return false;
  };

  const getStateName = () => {
    if (fieldArr && fieldArr?.includes("County")) return "County";
    if (fieldArr && fieldArr?.includes("State")) return "State";
    if (fieldArr && fieldArr?.includes("Department")) return "Department";
    if (fieldArr && fieldArr?.includes("District")) return "District";
    if (fieldArr && fieldArr?.includes("Emirate")) return "Emirate";
    if (fieldArr && fieldArr?.includes("Island")) return "Island";
    if (fieldArr && fieldArr?.includes("Oblast")) return "Oblast";
    if (fieldArr && fieldArr?.includes("Parish")) return "Parish";
    if (fieldArr && fieldArr?.includes("Prefecture")) return "Prefecture";
    if (fieldArr && fieldArr?.includes("Province")) return "Province";

    return false;
  };

  useEffect(() => {
    // if (!show) return;
    const getCountryAndState = async () => {
      try {
        const res: any = await dispatch(countryAndState()).unwrap();
        const data = res?.body?.data || [];
        setCountriesWithStates(data);
      } catch (error) {
        console.error(error);
      }
    };

    getCountryAndState();
  }, []);

  const getStatesForCountryCode = (code: string) => {
    try {
      const countryMeta = countryList?.find((c: any) => c?.value === code);
      const countryName = countryMeta?.name;
      if (!countryName) return [];
      const found = countriesWithStates?.find(
        (c: any) =>
          c?.country_name?.toLowerCase() === countryName?.toLowerCase()
      );
      const st = found?.states || [];
      return st.map((s: any) => ({
        name: s?.state_name,
        value: s?.state_name,
        state_code: s?.state_code,
      }));
    } catch (e) {
      return [];
    }
  };

  useEffect(() => {
    if (
      values?.x_country &&
      countriesWithStates?.length > 0 &&
      countryList?.length > 0
    ) {
      const nStates = getStatesForCountryCode(values?.x_country);
      setStates(nStates);
    }
  }, [values?.x_country, countriesWithStates, countryList]);

  const selectedCountryUI = values?.x_country ?? "";

  useEffect(() => {
    if (
      isEdit &&
      !initCountryRef.current &&
      selectedCountryUI &&
      countryList?.length > 0
    ) {
      const ev = countryList.find((c: any) => c?.value === selectedCountryUI);
      if (ev) {
        const nStates = getStatesForCountryCode(selectedCountryUI as string);
        setStates(nStates);
        handleChange(ev, "x_country");
        initCountryRef.current = true;
      }
    }
  }, [isEdit, selectedCountryUI, countryList, handleChange]);

  const imgUrl = (): React.ReactNode => {
    const cardType = values?.card_type || iconType;
    switch (cardType?.toLowerCase()) {
      case "visa":
        return <VisaIcon />;
      case "amex":
      case "american express":
      case "americanexpress":
      case "american expres":
        return <img src={amex} alt="AMEX" className="object-contain" />;
      case "mc":
      case "master":
      case "mastercard":
        return <MastercardIcon />;
      case "discover":
      case "ds":
        return <Discover />;
      case "jcb":
        return <img src={JCB} alt="JCB" className="object-contain" />;
      case "diners":
      case "dinersclub":
      case "diners club":
      case "dc":
        return <img src={dinersClub} alt="DC" />;
      case "unionpay":
        return <UnionPay />;
      default:
        return "";
    }
  };

  return (
    <>
      {/* Card details */}
      <div className="w-full" data-testid={tid("card-fields")}>
        <div className="relative">
          <InputField
            name="x_card_num"
            data-testid={tid("card-number-input")}
            aria-label="Card number"
            label="card information"
            className={`!pl-[15px] !pr-[15px] !py-2 !text-xs !rounded-[5px] !rounded-b-none  ${
              isErr === 1 ? "!border-b-1 !border-red" : "border-b-0 "
            }`}
            _className="!text-xs !font-bold !leading-normal"
            placeholder="1234  1234  1234  1234"
            value={cardNumber}
            onChange={(e) => handleChange(e, "x_card_num")}
            onBlur={validateCard}
            disabled={disableInputFields || isEdit}
          />
          <div
            className="absolute right-[11px] top-[30px] w-[35px] h-[18px] flex"
            aria-hidden="true"
          >
            {imgUrl()}
          </div>
        </div>
        <div className="grid grid-cols-2">
          <InputField
            name="x_exp_date"
            data-testid={tid("card-expiry-input")}
            aria-label="Card expiration date"
            showLabel={false}
            className={`!pl-[15px] !pr-[15px] !py-2 !text-xs !rounded-[5px] !rounded-t-none !rounded-e-none  ${
              isErr === 2 ? "!border-red" : isErr === 1 ? "!border-t-0" : ""
            }`}
            _className="!text-xs !font-bold !leading-normal"
            placeholder="MM / YY"
            value={values?.x_exp_date}
            onChange={(e) => {
              handleChange(e, "x_exp_date");
            }}
            onBlur={validateCard}
            disabled={disableInputFields || isEdit}
          />
          <InputField
            type={isEdit ? "text" : "number"}
            min={0}
            name="x_card_code"
            data-testid={tid("card-cvc-input")}
            aria-label="Card security code"
            showLabel={false}
            className={`!pl-[15px] !pr-[15px] !py-2 !text-xs !rounded-[5px] !rounded-t-none  !rounded-s-none  ${
              isErr === 3
                ? "!border-l-1 !border-red"
                : isErr === 1
                  ? "!border-t-0"
                  : "!border-l-0"
            }`}
            _className="!text-xs !font-bold !leading-normal"
            placeholder="CVC"
            value={isEdit ? "XXX" : values?.x_card_code}
            onChange={(e) => {
              handleChange(e, "x_card_code");
            }}
            onBlur={validateCard}
            disabled={disableInputFields || isEdit}
          />
        </div>
        {isErr !== -1 &&
          isErr !== 4 &&
          (isErr !== 3 ? (
            <div className="text-xs text-red">{cardErrMsg}</div>
          ) : (
            <div className="grid grid-cols-2">
              <div></div>
              <div className="text-xs text-red">{cardErrMsg}</div>
            </div>
          ))}
      </div>
      {/* Card holder name */}
      <div className="w-full">
        <InputField
          name="choldername"
          data-testid={tid("cardholder-name-input")}
          aria-label="Cardholder name"
          label="Cardholder name"
          className="!pl-[15px] !pr-[15px] !py-2 !text-xs !rounded-[5px] "
          _className="!text-xs !font-bold !leading-normal"
          placeholder="Full name on card"
          value={values?.choldername}
          onChange={(e) => handleChange(e, "choldername")}
          tabInd={4}
          disabled={disableInputFields}
        />
      </div>
      {/* ADDRESS */}
      {portalSettings?.cc_billing_address === SettingsValue.YES && (
        <div className="w-full">
          <InputSelect
            data-testid={tid("billing-country-select")}
            aria-label="Billing country"
            label="Billing Address"
            className="!pl-[15px] !pr-[15px] !py-2 !text-xs !rounded-[5px] !rounded-b-none "
            _className="!text-xs !font-bold !leading-normal"
            optionClassName="!text-xs"
            arrowClassName="!right-[11px] !top-[10px]"
            placeholder="Country"
            selected={selectedCountryUI}
            options={countryList}
            setSelected={(e: any) => {
              autoDetectRef.current = true;
              const nStates = getStatesForCountryCode(e?.value as string);
              setStates(nStates);
              if (values?.x_state) {
                const selectedStateObject: any = nStates.find(
                  (state: any) => state.name === values?.x_state
                );
                if (selectedStateObject) {
                  setSelectedState(selectedStateObject);
                  handleChange(selectedStateObject?.name, "x_state");
                } else {
                  const obj = nStates.find(
                    (state: any) => state.state_code === values?.x_state
                  );
                  if (obj) {
                    setSelectedState(obj);
                    handleChange(obj?.name, "x_state");
                  } else {
                    setSelectedState("");
                    handleChange({ target: { value: "" } }, "x_state");
                  }
                }
              } else {
                setSelectedState("");
                handleChange({ target: { value: "" } }, "x_state");
              }
              handleChange(e, "x_country");
            }}
            tabInd={5}
            disabled={disableInputFields}
          />
          {fieldArr && fieldArr.includes("Address line 1") && (
            <InputField
              name="x_address"
              data-testid={tid("billing-address-line-1-input")}
              aria-label="Billing address line 1"
              showLabel={false}
              className={`!pl-[15px] !pr-[15px] !py-2 !text-xs !border-t-0 ${
                (fieldArr &&
                  (fieldArr.includes("City") ||
                    fieldArr.includes("Area") ||
                    fieldArr.includes("Address line 2"))) ||
                getPostalCode() ||
                getStateName()
                  ? "!rounded-none"
                  : "!rounded-b-[5px] !rounded-t-none"
              }`}
              _className="!text-xs !font-bold !leading-normal"
              placeholder="Address line 1"
              value={values?.x_address}
              onChange={(e) => handleChange(e, "x_address")}
              tabInd={6}
              disabled={disableInputFields}
            />
          )}
          {fieldArr && fieldArr.includes("Address line 2") && (
            <InputField
              name="address2"
              data-testid={tid("billing-address-line-2-input")}
              aria-label="Billing address line 2"
              showLabel={false}
              className={`!pl-[15px] !pr-[15px] !py-2 !text-xs  !border-t-0 ${
                (fieldArr &&
                  (fieldArr.includes("City") || fieldArr.includes("Area"))) ||
                getPostalCode() ||
                getStateName()
                  ? "!rounded-none"
                  : "!rounded-b-[5px] !rounded-t-none"
              }`}
              _className="!text-xs !font-bold !leading-normal"
              placeholder="Address line 2"
              value={values?.address2}
              onChange={(e) => handleChange(e, "address2")}
              tabInd={7}
              disabled={disableInputFields}
            />
          )}
          <div
            className={`grid ${
              getPostalCode()
                ? !fieldArr?.includes("City") && !fieldArr?.includes("Area")
                  ? "grid-cols-1"
                  : "grid-cols-2"
                : "grid-cols-1"
            }`}
          >
            {fieldArr && fieldArr.includes("City") && (
              <InputField
                name="x_city"
                data-testid={tid("billing-city-input")}
                aria-label="Billing city"
                showLabel={false}
                className={`!pl-[15px] !pr-[15px] !py-2 !text-xs !rounded-t-none !border-t-0 ${
                  getStateName()
                    ? "!rounded-none"
                    : getPostalCode()
                      ? "rounded-b-[5px] !rounded-r-none"
                      : "!rounded-[5px] !rounded-t-none"
                }`}
                _className="!text-xs !font-bold !leading-normal"
                placeholder="City"
                value={values?.x_city}
                onChange={(e) => handleChange(e, "x_city")}
                tabInd={8}
                disabled={disableInputFields}
              />
            )}
            {fieldArr && fieldArr.includes("Area") && (
              <InputField
                name="x_city"
                data-testid={tid("billing-area-input")}
                aria-label="Billing area"
                showLabel={false}
                className={`!pl-[15px] !pr-[15px] !py-2 !text-xs !rounded-t-none !border-t-0 ${
                  getStateName()
                    ? "!rounded-none"
                    : getPostalCode()
                      ? "rounded-b-[5px] !rounded-r-none"
                      : "!rounded-[5px] !rounded-t-none"
                }`}
                _className="!text-xs !font-bold !leading-normal"
                placeholder="Area"
                value={values?.x_city}
                onChange={(e) => handleChange(e, "x_city")}
                tabInd={8}
                disabled={disableInputFields}
              />
            )}

            {/* ['CEDEX' , 'ZIP' , 'Eircode' , 'PIN', "Postal code" ] */}
            {getPostalCode() && (
              <InputField
                name="x_zip"
                data-testid={tid("billing-postal-code-input")}
                aria-label="Billing postal code"
                type={fieldObj?.isNumber ? "number" : "text"}
                showLabel={false}
                className={`!pl-[15px] !pr-[15px] !py-2 !text-xs !border-t-0 ${
                  !fieldArr?.includes("City") && !fieldArr?.includes("Area")
                    ? ""
                    : "!border-l-0"
                } ${
                  getStateName()
                    ? "!rounded-none"
                    : "!rounded-b-[5px] !rounded-l-none !rounded-t-none"
                }`}
                _className="!text-xs !font-bold !leading-normal"
                placeholder={getPostalCode() || ""}
                value={values?.x_zip}
                onChange={(e) => {
                  if (e.target.value?.length <= (fieldObj?.zipLength || 50))
                    handleChange(e, "x_zip");
                }}
                tabInd={9}
                disabled={disableInputFields}
              />
            )}
          </div>
          {states && states.length > 0 && (
            <InputSelect
              data-testid={tid("billing-state-select")}
              aria-label="Billing state"
              showLabel={false}
              className="!pl-[15px] !pr-[15px] !py-2 !text-xs !rounded-[5px] !rounded-t-none !border-t-0 "
              _className="!text-xs !font-bold !leading-normal"
              optionClassName="!text-xs"
              placeholder={getStateName() || "State"}
              selected={selectedState}
              arrowClassName="!right-[11px] !top-[10px]"
              isManual
              options={states}
              setSelected={(e: any) => {
                if (e?.value) {
                  setSelectedState(e);
                  handleChange(e, "x_state");
                }
              }}
              tabInd={10}
              disabled={disableInputFields}
            />
          )}
        </div>
      )}

      {portalSettings?.cc_billing_address === SettingsValue.NO && (
        <div className="w-full">
          <InputSelect
            data-testid={tid("billing-country-select")}
            aria-label="Billing country"
            label="Billing Address"
            className="!pl-[15px] !pr-[15px] !py-2 !text-xs !rounded-[5px] !rounded-b-none "
            _className="!text-xs !font-bold !leading-normal"
            optionClassName="!text-xs"
            arrowClassName="!right-[11px] !top-[10px]"
            placeholder="Country"
            selected={selectedCountryUI}
            options={countryList}
            setSelected={(e: any) => {
              autoDetectRef.current = true;
              const nStates = getStatesForCountryCode(e?.value as string);
              setStates(nStates);
              if (values?.x_state) {
                const selectedStateObject: any = nStates.find(
                  (state: any) => state.name === values?.x_state
                );
                if (selectedStateObject) {
                  setSelectedState(selectedStateObject);
                  handleChange(selectedStateObject?.name, "x_state");
                } else {
                  const obj = nStates.find(
                    (state: any) => state.state_code === values?.x_state
                  );
                  if (obj) {
                    setSelectedState(obj);
                    handleChange(obj?.name, "x_state");
                  } else {
                    setSelectedState("");
                    handleChange({ target: { value: "" } }, "x_state");
                  }
                }
              } else {
                setSelectedState("");
                handleChange({ target: { value: "" } }, "x_state");
              }
              handleChange(e, "x_country");
            }}
            tabInd={5}
            disabled={disableInputFields}
          />
          {getPostalCode() && (
            <InputField
              name="x_zip"
              data-testid={tid("billing-postal-code-input")}
              aria-label="Billing postal code"
              type={fieldObj?.isNumber ? "number" : "text"}
              showLabel={false}
              className="!pl-[15px] !pr-[15px] !py-2 !text-xs !rounded-b-[5px] !border-t-0 !rounded-t-none "
              _className="!text-xs !font-bold !leading-normal"
              placeholder={getPostalCode() || ""}
              value={values?.x_zip}
              onChange={(e) => {
                if (e.target.value?.length <= (fieldObj?.zipLength || 50))
                  handleChange(e, "x_zip");
              }}
              tabInd={9}
              disabled={disableInputFields}
            />
          )}
        </div>
      )}
    </>
  );
};

export default CardFormComponent;
